import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "@/lib/query-client";
import { HelmetProvider } from "react-helmet-async";
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from "react-router-dom";
import { PageSkeleton } from "@/components/PageSkeleton";
import "@/styles/insights.css";

const InsightsIndex = lazy(() => import("./pages/insights/InsightsIndex"));
const InsightsArticle = lazy(() => import("./pages/insights/InsightsArticle"));
const InsightsPreview = lazy(() => import("./pages/insights/InsightsPreview"));
const ContentDashboard = lazy(() => import("./pages/admin/ContentDashboard"));
const ContentEditor = lazy(() => import("./pages/admin/ContentEditor"));

import { AuthProvider } from "@/contexts/AuthContext";
// Only /app/* uses this guard, and it pulls the backend client plus brand /
// subscription hooks. Lazy so public marketing pages never download it.
const ReportOnlyRedirect = lazy(() =>
  import("@/components/reports/ReportOnlyRedirect").then((m) => ({
    default: m.ReportOnlyRedirect,
  })),
);
// All page components are lazy-loaded so the initial marketing bundle
// stays small and admin/app/JV surfaces ship as separate chunks.
// The public marketing layout stays static (it is the first paint for most
// visitors); the signed-in app, report, academy and admin shells are lazy so
// their dependency graphs (sidebar, charts, upgrade drawer, assistant) never
// land in the entry chunk downloaded by marketing visitors.
import { RouteErrorBoundary } from "@/components/RouteErrorBoundary";
import { PublicLayout } from "@/layouts/PublicLayout";

const AppLayout = lazy(() => import("./layouts/AppLayout").then((m) => ({ default: m.AppLayout })));
const ReportLayout = lazy(() =>
  import("./layouts/ReportLayout").then((m) => ({ default: m.ReportLayout })),
);
const AcademyLayout = lazy(() =>
  import("./layouts/AcademyLayout").then((m) => ({ default: m.AcademyLayout })),
);
const AdminRouteGuard = lazy(() =>
  import("./components/admin/AdminRouteGuard").then((m) => ({ default: m.AdminRouteGuard })),
);
const PlanSwitcher = lazy(() =>
  import("./components/dev/PlanSwitcher").then((m) => ({ default: m.PlanSwitcher })),
);

const ReportArchive = lazy(() => import("./pages/reports/ReportArchive"));
const MyReports = lazy(() => import("./pages/reports/MyReports"));
const AddReport = lazy(() => import("./pages/reports/AddReport"));
const ReportSubscriptionHome = lazy(() => import("./pages/reports/ReportSubscriptionHome"));
const ReportManage = lazy(() => import("./pages/reports/ReportManage"));
const ReportDetail = lazy(() => import("./pages/reports/ReportDetail"));
const ReportAccount = lazy(() => import("./pages/reports/ReportAccount"));
const ReportBilling = lazy(() => import("./pages/reports/ReportBilling"));
const Index = lazy(() => import("./pages/Index"));
// Experimental visual-first homepage (V2). Production homepage above is unchanged.
const HomeV2 = lazy(() => import("./pages/HomeV2"));
const Pricing = lazy(() => import("./pages/Pricing"));
const DemoIndex = lazy(() => import("./pages/demo/DemoIndex"));
const DemoDashboard = lazy(() => import("./pages/demo/DemoDashboard"));
const AdLanding = lazy(() => import("./pages/plans/AdLanding"));
const MonthlyReportCheckout = lazy(() => import("./pages/plans/MonthlyReportCheckout"));
const CheckoutProcessing = lazy(() => import("./pages/checkout/CheckoutProcessing"));
const CheckoutClaim = lazy(() => import("./pages/checkout/CheckoutClaim"));
const Methodology = lazy(() => import("./pages/Methodology"));
const AiAuthorityIndex = lazy(() => import("./pages/AiAuthorityIndex"));
const Security = lazy(() => import("./pages/Security"));
const HowItWorks = lazy(() => import("./pages/HowItWorks"));
const About = lazy(() => import("./pages/company/About"));
const Contact = lazy(() => import("./pages/company/Contact"));
const Careers = lazy(() => import("./pages/company/Careers"));
const Privacy = lazy(() => import("./pages/legal/Privacy"));
const Terms = lazy(() => import("./pages/legal/Terms"));
const Cookies = lazy(() => import("./pages/legal/Cookies"));
const RefundPolicy = lazy(() => import("./pages/legal/RefundPolicy"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const Scan = lazy(() => import("./pages/Scan"));
const ScanResult = lazy(() => import("./pages/ScanResult"));
const ScanPending = lazy(() => import("./pages/ScanPending"));
const ExecutiveBrief = lazy(() => import("./pages/ExecutiveBrief"));
const AwinTestCheckout = lazy(() => import("./pages/AwinTestCheckout"));
const AwinTestSuccess = lazy(() => import("./pages/AwinTestSuccess"));
const SearchPage = lazy(() => import("./pages/Search"));
const CompanyProfile = lazy(() => import("./pages/CompanyProfile"));
const CompareIndex = lazy(() => import("./pages/compare/CompareIndex"));
const CompareDispatcher = lazy(() => import("./pages/compare/CompareDispatcher"));
const Login = lazy(() => import("./pages/Login"));
const Waitlist = lazy(() => import("./pages/Waitlist"));
const MarketMap = lazy(() => import("./pages/MarketMap"));
const Recommend = lazy(() => import("./pages/Recommend"));
const AcademyIndex = lazy(() => import("./pages/academy/Index"));
const AcademySection = lazy(() => import("./pages/academy/Section"));
const AcademyArticle = lazy(() => import("./pages/academy/Article"));
const AcademyGlossary = lazy(() => import("./pages/academy/Glossary"));
const AcademyReports = lazy(() => import("./pages/academy/Reports"));
const AcademyFaq = lazy(() => import("./pages/academy/Faq"));
const EmailFirstAuth = lazy(() => import("./pages/auth/EmailFirstAuth"));
const ForgotPassword = lazy(() => import("./pages/auth/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/auth/ResetPassword"));
const AuthCallback = lazy(() => import("./pages/auth/Callback"));
const LinkExpired = lazy(() => import("./pages/auth/LinkExpired"));
// App pages — code-split so the initial app-shell bundle stays small.
// Loading fallback is the shared PageSkeleton (identical to previous
// per-page skeletons); no data-fetch or business-logic changes.
const Overview = lazy(() => import("./pages/app/Overview"));
const Prompts = lazy(() => import("./pages/app/Prompts"));
const Categories = lazy(() => import("./pages/app/Categories"));
const Competitors = lazy(() => import("./pages/app/Competitors"));
const Opportunities = lazy(() => import("./pages/app/Opportunities"));
const AiPerception = lazy(() => import("./pages/app/AiPerception"));
const Reports = lazy(() => import("./pages/app/Reports"));
const AppSettings = lazy(() => import("./pages/app/Settings"));
const Onboarding = lazy(() => import("./pages/app/Onboarding"));

// Alerts page temporarily hidden from customer-facing product. Import retained (commented) for easy restore.
// const Alerts = lazy(() => import("./pages/app/Alerts"));
const Recommendations = lazy(() => import("./pages/app/Recommendations"));



const AdminScans = lazy(() => import("./pages/admin/Scans"));
const AdminDashboard = lazy(() => import("./pages/admin/Dashboard"));
const AdminCostDashboard = lazy(() => import("./pages/admin/CostDashboard"));
const AdminScanQA = lazy(() => import("./pages/admin/ScanQA"));
const AdminFreeScanIntel = lazy(() => import("./pages/admin/FreeScanIntel"));
const AdminFreeScanLeads = lazy(() => import("./pages/admin/FreeScanLeads"));
const AdminFreeScanFunnel = lazy(() => import("./pages/admin/FreeScanFunnel"));
const AdminFreeScanIntelDetail = lazy(() => import("./pages/admin/FreeScanIntelDetail"));
const AdminFreeScanCalibration = lazy(() => import("./pages/admin/FreeScanCalibration"));
const Phase2ValidationPreview = lazy(() => import("./pages/admin/Phase2ValidationPreview"));
const AdminAssistantAnalytics = lazy(() => import("./pages/admin/AssistantAnalytics"));
const AdminEmailDashboard = lazy(() => import("./pages/admin/EmailDashboard"));
const AdminSupport = lazy(() => import("./pages/admin/Support"));
const AdminSupportConversation = lazy(() => import("./pages/admin/SupportConversation"));
const AdminContextIntelligence = lazy(() => import("./pages/admin/ContextIntelligence"));
const AdminConversions = lazy(() => import("./pages/admin/Conversions"));
const AdminInsightsSearchAnalytics = lazy(() => import("./pages/admin/InsightsSearchAnalytics"));
const AdminCreatorProgram = lazy(() => import("./pages/admin/CreatorProgram"));
const CreatorDashboard = lazy(() => import("./pages/creator/CreatorDashboard"));
const CreatorPayoutStatement = lazy(() => import("./pages/creator/CreatorPayoutStatement"));
import { AdminPreviewProvider } from "@/contexts/AdminPreviewContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { FEATURES } from "@/config/features";
import { PageTracker } from "@/components/analytics/PageTracker";
import { ConsentBanner } from "@/components/consent/ConsentBanner";
import { AuthErrorFragmentCatcher } from "@/components/auth/AuthErrorFragmentCatcher";
import { initConsent } from "@/lib/consent";
import { PaymentModeBanner } from "@/components/dev/PaymentModeBanner";
import { logPaymentModeBanner } from "@/lib/payment-mode";
import { IndexPointerTracker } from "@/components/tracking/IndexPointerTracker";

initConsent();
logPaymentModeBanner();


const App = () => (
  <QueryClientProvider client={queryClient}>
    <HelmetProvider>
      <AuthProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <PaymentModeBanner />
          {import.meta.env.DEV && (
            <Suspense fallback={null}>
              <PlanSwitcher />
            </Suspense>
          )}
          <BrowserRouter>
            <PageTracker />
            <AuthErrorFragmentCatcher />
            <IndexPointerTracker />
            <ConsentBanner />
            <Suspense fallback={<PageSkeleton />}>
            <Routes>
              {/* Public static sales demos — frozen data, no auth, no backend */}
              <Route path="/demo" element={<DemoIndex />} />
              <Route path="/demo/:slug" element={<DemoDashboard />} />
              {/* Public marketing */}
              <Route element={<PublicLayout />}>
                <Route path="/" element={<HomeV2 />} />
                {/* Spanish marketing homepage (public marketing only). */}
                <Route path="/es" element={<HomeV2 locale="es" />} />
                <Route path="/home-legacy" element={<Index />} />
                <Route path="/home-v2" element={<Navigate to="/" replace />} />

                <Route path="/pricing" element={<Pricing />} />
                <Route path="/es/pricing" element={<Pricing locale="es" />} />
                {/* Advertising landing pages (not in navigation). Old URLs redirect reversibly. */}
                <Route path="/core" element={<AdLanding product="core" />} />
                <Route path="/es/core" element={<AdLanding product="core" locale="es" />} />
                <Route path="/pro" element={<AdLanding product="pro" />} />
                <Route path="/es/pro" element={<AdLanding product="pro" locale="es" />} />
                <Route path="/ai-authority-report" element={<AdLanding product="report" />} />
                <Route path="/es/ai-authority-report" element={<AdLanding product="report" locale="es" />} />
                <Route path="/starter" element={<Navigate to="/core" replace />} />
                <Route path="/growth" element={<Navigate to="/pro" replace />} />
                <Route path="/enterprise" element={<Navigate to="/pricing" replace />} />
                <Route path="/ai-visibility-report" element={<Navigate to="/ai-authority-report" replace />} />
                <Route path="/monthly-report" element={<Navigate to="/ai-authority-report" replace />} />
                <Route path="/methodology" element={<Methodology />} />
                <Route path="/ai-authority-index" element={<AiAuthorityIndex />} />
                <Route path="/security" element={<Security />} />
                <Route path="/product" element={<Navigate to="/" replace />} />
                <Route path="/how-it-works" element={<HowItWorks />} />
                <Route path="/company/about" element={<About />} />
                <Route path="/company/contact" element={<Contact />} />
                <Route path="/company/careers" element={<Careers />} />
                <Route path="/legal/privacy" element={<Privacy />} />
                <Route path="/legal/terms" element={<Terms />} />
                <Route path="/legal/cookies" element={<Cookies />} />
                <Route path="/refund-policy" element={<RefundPolicy />} />
                <Route path="/scan" element={<Navigate to="/free-ai-visibility-scan" replace />} />
                <Route path="/scan/result/:scanId" element={FEATURES.FREE_SCAN_ENABLED ? <RouteErrorBoundary routeLabel="/scan/result/:scanId"><ScanResult /></RouteErrorBoundary> : <Navigate to="/free-ai-visibility-scan" replace />} />
                <Route path="/search" element={<SearchPage />} />
                <Route path="/ai-authority/:slug" element={<CompanyProfile />} />
                <Route path="/compare" element={<CompareIndex />} />
                <Route path="/compare/:slugs" element={<CompareDispatcher />} />
                <Route path="/market-map" element={<MarketMap />} />
                <Route path="/partners" element={<Recommend />} />
                <Route path="/recommend" element={<Navigate to="/partners" replace />} />
                <Route path="/affiliates" element={<Navigate to="/partners" replace />} />

                {/* Content Creator program — read-only creator surface (existing auth) */}
                <Route path="/creator" element={<ProtectedRoute><CreatorDashboard /></ProtectedRoute>} />
                <Route
                  path="/creator/payouts/:payoutId"
                  element={<ProtectedRoute><CreatorPayoutStatement /></ProtectedRoute>}
                />

                {/* Academy — educational hub */}
                <Route path="/academy" element={<AcademyIndex />} />
                <Route path="/academy" element={<AcademyLayout />}>
                  <Route path="glossary" element={<AcademyGlossary />} />
                  <Route path="reports" element={<AcademyReports />} />
                  <Route path="faq" element={<AcademyFaq />} />
                  <Route path=":section" element={<AcademySection />} />
                  <Route path=":section/:slug" element={<AcademyArticle />} />
                </Route>
              </Route>

              {/* Free AI Visibility Scan — immersive dark, no PublicLayout chrome */}
              <Route path="/free-ai-visibility-scan" element={<Scan />} />
              <Route path="/free-ai-visibility-scan/pending" element={<ScanPending />} />
                <Route path="/es/free-ai-visibility-scan" element={<Scan locale="es" />} />
                <Route path="/es/free-ai-visibility-scan/pending" element={<ScanPending locale="es" />} />

              {/* Insights CMS — light editorial theme, own layout */}
              <Route path="/insights" element={<InsightsIndex />} />
              <Route path="/insights/:slug" element={<InsightsArticle />} />
              <Route path="/admin/content/preview/:id" element={<AdminRouteGuard><InsightsPreview /></AdminRouteGuard>} />
              <Route path="/admin/content" element={<AdminRouteGuard><ContentDashboard /></AdminRouteGuard>} />
              <Route path="/admin/content/new" element={<AdminRouteGuard><ContentEditor /></AdminRouteGuard>} />
              <Route path="/admin/content/:id" element={<AdminRouteGuard><ContentEditor /></AdminRouteGuard>} />

              {/* Executive one-pager — standalone, print-optimized */}
              <Route path="/one-pager" element={<ExecutiveBrief />} />
              <Route path="/executive-brief" element={<Navigate to="/one-pager" replace />} />

              {/* JVZoo affiliate funnel — standalone, isolated from main marketing site */}
              {/* Retired JVZoo affiliate funnel — all campaign paths now
                  resolve to canonical pricing. */}
              <Route path="/jv/*" element={<PreserveQueryRedirect to="/pricing" />} />
              <Route path="/jv" element={<PreserveQueryRedirect to="/pricing" />} />
              <Route path="/jvzoo/*" element={<PreserveQueryRedirect to="/pricing" />} />
              <Route path="/jvzoo" element={<PreserveQueryRedirect to="/pricing" />} />
              <Route path="/offer" element={<PreserveQueryRedirect to="/pricing" />} />
              <Route path="/ai-visibility-offer" element={<PreserveQueryRedirect to="/pricing" />} />

              {/* Conversion-tracking QA harness (RetailerBoost / GTM).
                  Disable by setting ALLOW_TEST_CHECKOUT=false and
                  ALLOW_RB_TEST_CHECKOUT=false on the server. */}
              <Route path="/awin-test-checkout" element={<AwinTestCheckout />} />
              <Route path="/awin-test-success" element={<AwinTestSuccess />} />

              {/* Waitlist (public gate) */}
              <Route path="/waitlist" element={<Waitlist />} />
              <Route path="/unsubscribe" element={<Unsubscribe />} />


              {/* Early access / waitlist (legacy) */}
              <Route path="/early-access" element={<Login />} />
              <Route path="/login" element={<Navigate to="/waitlist" replace />} />
              <Route path="/signup" element={<Navigate to="/waitlist" replace />} />

              {/* Auth — single email-first flow. Legacy paths render the same component. */}
              {/* Report purchase-intent checkout step (no marketing nav) */}
              <Route path="/ai-visibility-report/checkout" element={<MonthlyReportCheckout />} />
              {/* Payment-first: verified fulfilment wait + secure account claim. */}
              <Route path="/checkout/processing" element={<CheckoutProcessing />} />
              <Route path="/checkout/claim" element={<CheckoutClaim />} />

              <Route path="/auth" element={<EmailFirstAuth />} />
              <Route path="/auth/sign-in" element={<EmailFirstAuth prefillMode="signin" />} />
              <Route path="/auth/sign-up" element={<EmailFirstAuth prefillMode="signup" />} />
              <Route path="/auth/forgot-password" element={<ForgotPassword />} />
              <Route path="/auth/reset-password" element={<ResetPassword />} />
              <Route path="/auth/callback" element={<AuthCallback />} />
              {/* Canonical recovery state for expired/invalid email links. */}
              <Route path="/auth/link-expired" element={<LinkExpired />} />


              {/* Standalone Monthly AI Visibility Report experience (no dashboard nav) */}
              <Route element={<ReportLayout />}>
                {/* M5 — multi-Report hub. `/reports` is the canonical entry. */}
                <Route path="/reports" element={<MyReports />} />
                <Route path="/reports/new" element={<AddReport />} />
                <Route path="/reports/s/:reportSubscriptionId" element={<ReportSubscriptionHome />} />
                <Route
                  path="/reports/s/:reportSubscriptionId/manage"
                  element={<ReportManage />}
                />
                {/* Legacy flat archive kept reachable for deep links. */}
                <Route path="/reports/archive" element={<ReportArchive />} />
                <Route path="/reports/:id" element={<ReportDetail />} />
                <Route path="/account" element={<ReportAccount />} />
                <Route path="/billing" element={<ReportBilling />} />
              </Route>

              {/* Protected app with sidebar layout */}
              <Route path="/app" element={<ReportOnlyRedirect><AppLayout /></ReportOnlyRedirect>}>
                <Route path="onboarding" element={<Onboarding />} />
                <Route path="overview" element={<Overview />} />
                <Route path="snapshot" element={<Navigate to="/app/overview" replace />} />
                <Route path="prompts" element={<Prompts />} />
                <Route path="categories" element={<Categories />} />
                <Route path="competitors" element={<Competitors />} />
                <Route path="positioning" element={<Navigate to="/app/competitors" replace />} />
                <Route path="opportunities" element={<Opportunities />} />
                <Route path="ai-perception" element={<AiPerception />} />
                <Route path="reports" element={<Reports />} />
                {/* Alerts temporarily hidden — engine not yet production-ready. Redirect any old links. */}
                <Route path="alerts" element={<Navigate to="/app/overview" replace />} />
                <Route path="recommendations" element={<Recommendations />} />
                
                <Route path="settings" element={<AppSettings />} />
              </Route>

              {/* Admin */}
              <Route path="/admin" element={<AdminRouteGuard><AdminDashboard /></AdminRouteGuard>} />
              <Route path="/admin/scans" element={<AdminRouteGuard><AdminScans /></AdminRouteGuard>} />
              <Route path="/admin/costs" element={<AdminRouteGuard><AdminCostDashboard /></AdminRouteGuard>} />
              <Route path="/admin/scan-qa" element={<AdminRouteGuard><AdminScanQA /></AdminRouteGuard>} />
              <Route path="/admin/free-scan-intel" element={<AdminRouteGuard><AdminFreeScanIntel /></AdminRouteGuard>} />
              <Route path="/admin/free-scan-intel/:scanId" element={<AdminRouteGuard><AdminFreeScanIntelDetail /></AdminRouteGuard>} />
              <Route path="/admin/free-scan-leads" element={<AdminRouteGuard><AdminFreeScanLeads /></AdminRouteGuard>} />
              <Route path="/admin/free-scan-funnel" element={<AdminRouteGuard><AdminFreeScanFunnel /></AdminRouteGuard>} />
              <Route path="/admin/free-scan-calibration" element={<AdminRouteGuard><AdminFreeScanCalibration /></AdminRouteGuard>} />

              <Route path="/admin/phase2-validation-preview" element={<AdminRouteGuard><Phase2ValidationPreview /></AdminRouteGuard>} />
              <Route path="/admin/assistant-analytics" element={<AdminRouteGuard><AdminAssistantAnalytics /></AdminRouteGuard>} />
              <Route path="/admin/emails" element={<AdminRouteGuard><AdminEmailDashboard /></AdminRouteGuard>} />
              <Route path="/admin/conversions" element={<AdminRouteGuard><AdminConversions /></AdminRouteGuard>} />
              <Route path="/admin/insights-search" element={<AdminRouteGuard><AdminInsightsSearchAnalytics /></AdminRouteGuard>} />
              <Route path="/admin/creators" element={<AdminRouteGuard><AdminCreatorProgram /></AdminRouteGuard>} />

              <Route path="/admin/support" element={<AdminRouteGuard><AdminSupport /></AdminRouteGuard>} />
              <Route path="/admin/context-intelligence" element={<AdminRouteGuard><AdminContextIntelligence /></AdminRouteGuard>} />
              <Route path="/admin/support/:conversationId" element={<AdminRouteGuard><AdminSupportConversation /></AdminRouteGuard>} />
              <Route path="/admin/accounts/:brandId" element={<AdminRouteGuard><AdminAccountDashboardPreview /></AdminRouteGuard>}>
                <Route index element={<Navigate to="dashboard" replace />} />
              </Route>
              <Route path="/admin/accounts/:brandId/dashboard" element={<AdminRouteGuard><AdminAccountDashboardPreview /></AdminRouteGuard>}>
                <Route index element={<Overview />} />
              </Route>
              <Route path="/admin/accounts/:brandId/prompts" element={<AdminRouteGuard><AdminAccountDashboardPreview /></AdminRouteGuard>}>
                <Route index element={<Prompts />} />
              </Route>
              <Route path="/admin/accounts/:brandId/categories" element={<AdminRouteGuard><AdminAccountDashboardPreview /></AdminRouteGuard>}>
                <Route index element={<Categories />} />
              </Route>
              <Route path="/admin/accounts/:brandId/competitors" element={<AdminRouteGuard><AdminAccountDashboardPreview /></AdminRouteGuard>}>
                <Route index element={<Competitors />} />
              </Route>
              <Route path="/admin/accounts/:brandId/positioning" element={<AdminRouteGuard><AdminAccountDashboardPreview /></AdminRouteGuard>}>
                <Route index element={<Navigate to="../competitors" replace />} />
              </Route>
              <Route path="/admin/accounts/:brandId/opportunities" element={<AdminRouteGuard><AdminAccountDashboardPreview /></AdminRouteGuard>}>
                <Route index element={<Opportunities />} />
              </Route>
              <Route path="/admin/accounts/:brandId/reports" element={<AdminRouteGuard><AdminAccountDashboardPreview /></AdminRouteGuard>}>
                <Route index element={<Reports />} />
              </Route>
              {/* Alerts temporarily hidden in admin preview to match customer experience. */}
              <Route path="/admin/accounts/:brandId/alerts" element={<AdminRouteGuard><AdminAccountDashboardPreview /></AdminRouteGuard>}>
                <Route index element={<Navigate to="../dashboard" replace />} />
              </Route>
              <Route path="/admin/accounts/:brandId/recommendations" element={<AdminRouteGuard><AdminAccountDashboardPreview /></AdminRouteGuard>}>
                <Route index element={<Recommendations />} />
              </Route>
              <Route path="/admin/accounts/:brandId/settings" element={<AdminRouteGuard><AdminAccountDashboardPreview /></AdminRouteGuard>}>
                <Route index element={<AppSettings />} />
              </Route>

              <Route path="*" element={<NotFound />} />
            </Routes>
            </Suspense>
          </BrowserRouter>
        </TooltipProvider>
      </AuthProvider>
    </HelmetProvider>
  </QueryClientProvider>
);

/** Redirects to `to` while preserving the current query string (affiliate click ids, utm_*). */
function PreserveQueryRedirect({ to }: { to: string }) {
  const { search } = useLocation();
  return <Navigate to={`${to}${search}`} replace />;
}

function AdminAccountDashboardPreview() {
  const { brandId } = useParams<{ brandId: string }>();

  if (!brandId) return <Navigate to="/admin" replace />;

  return (
    <AdminPreviewProvider previewBrandId={brandId}>
      <AppLayout />
    </AdminPreviewProvider>
  );
}

export default App;